import React, { useState } from 'react';
import { Button, Chip, Dialog, Notice, Panel, StatusDot, Switch } from '@origo/origo-ui';
import { MapTrifoldIcon, PencilSimpleIcon, SlidersHorizontalIcon, TrashIcon } from '@phosphor-icons/react';
import { useDeleteBinding, useUpdateBinding } from '../../api/hooks';
import { useDatasetPreview } from '../common/useDatasetPreview';
import { SyncTrigger } from './SyncTrigger';
import { ScheduleEditor } from './ScheduleEditor';
import {
    DIRECTION_POLICY_LABEL,
    policyAllows,
    SyncDirection,
    type SyncBinding,
    SyncDirectionPolicy,
} from '../../api/types';
import { InitialLoad } from './InitialLoad';
import { toUpsertRequest } from './bindingRequest';
import { ApiError } from '../../api/client';
import styles from '../common/panel.module.css';

interface BindingDetailProps {
    binding: SyncBinding;
    onBack: () => void;
    onEdit: () => void;
    /** Åpne mapping-editoren. */
    onEditMapping: () => void;
    onDeleted: () => void;
}

function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleString('nb-NO', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return iso;
    }
}

export const BindingDetail: React.FC<BindingDetailProps> = ({ binding, onBack, onEdit, onEditMapping, onDeleted }) => {
    const updateMutation = useUpdateBinding();
    const deleteMutation = useDeleteBinding();
    const { preview, isLoading: previewLoading, error: previewError } = useDatasetPreview();
    const [confirmDelete, setConfirmDelete] = useState(false);

    // toUpsertRequest sender med den lagrede mapping-profilen, slik at en enkel av/på-veksling
    // ikke får API-et til å generere en fersk profil og overskrive korreksjoner i mappingen.
    const toggleEnabled = (checked: boolean) => {
        updateMutation.mutate({
            bindingId: binding.bindingId,
            body: toUpsertRequest(binding, { enabled: checked }),
        });
    };

    const doDelete = async () => {
        try {
            await deleteMutation.mutateAsync(binding.bindingId);
            setConfirmDelete(false);
            onDeleted();
        } catch {
            /* feil vises under */
        }
    };

    const deleteError = deleteMutation.error instanceof ApiError ? deleteMutation.error.message : null;
    const policy = binding.directionPolicy ?? SyncDirectionPolicy.Both;

    return (
        <Panel heading={binding.displayName} onBack={onBack} flush>
            <div className={styles.scroll}>
                <div className={styles.chipRow}>
                    <StatusDot
                        variant={binding.enabled ? 'success' : 'warning'}
                        size="md"
                        label={binding.enabled ? 'Aktivert' : 'Deaktivert'}
                    >
                        {binding.enabled ? 'Aktivert' : 'Deaktivert'}
                    </StatusDot>
                    <Chip variant="info">{binding.forvaltningssystem}</Chip>
                    <Chip variant={policy === SyncDirectionPolicy.Both ? 'info' : 'warning'}>
                        {DIRECTION_POLICY_LABEL[policy]}
                    </Chip>
                    {!binding.mappingProfileJson && (
                        <Chip variant="warning">Mangler felt-mapping</Chip>
                    )}
                </div>

                <div className={styles.metaGrid}>
                    <span className={styles.metaLabel}>Kobling-ID</span>
                    <span className={`${styles.metaValue} ${styles.mono}`}>{binding.bindingId}</span>
                    <span className={styles.metaLabel}>Sentralt datasett</span>
                    <span className={`${styles.metaValue} ${styles.mono}`}>{binding.centralDatasetId}</span>
                    <span className={styles.metaLabel}>Retning</span>
                    <span className={styles.metaValue}>{DIRECTION_POLICY_LABEL[policy]}</span>
                    <span className={styles.metaLabel}>LokalId-felt</span>
                    <span className={styles.metaValue}>{binding.lokalIdField}</span>
                    <span className={styles.metaLabel}>AGOL-URL</span>
                    <span className={styles.metaValue}>{binding.agolBaseUrl}</span>
                    {binding.companionTableUrl && (
                        <>
                            <span className={styles.metaLabel}>Companion-tabell</span>
                            <span className={styles.metaValue}>{binding.companionTableUrl}</span>
                        </>
                    )}
                    <span className={styles.metaLabel}>Editor-tracking</span>
                    <span className={styles.metaValue}>
                        {binding.agolEditorField && binding.agolEditDateField
                            ? `${binding.agolEditorField} / ${binding.agolEditDateField}`
                            : 'mangler — konfliktdeteksjonen er blind'}
                    </span>
                    <span className={styles.metaLabel}>Opprettet</span>
                    <span className={styles.metaValue}>{formatDate(binding.createdUtc)}</span>
                    <span className={styles.metaLabel}>Oppdatert</span>
                    <span className={styles.metaValue}>{formatDate(binding.updatedUtc)}</span>
                </div>

                <div className={styles.rowActions}>
                    <Switch label="Aktivert" checked={binding.enabled} onChange={toggleEnabled} />
                </div>

                <div className={styles.rowActions}>
                    <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<MapTrifoldIcon size={16} weight="bold" />}
                        onClick={() => preview(binding.forvaltningssystem, binding.centralDatasetId, binding.displayName)}
                        disabled={previewLoading}
                    >
                        {previewLoading ? 'Henter…' : 'Vis i kart'}
                    </Button>
                    <Button variant="secondary" size="sm" leftIcon={<PencilSimpleIcon size={16} weight="bold" />} onClick={onEdit}>
                        Rediger
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<SlidersHorizontalIcon size={16} weight="bold" />}
                        onClick={onEditMapping}
                    >
                        Rediger mapping
                    </Button>
                </div>

                {previewError && <Notice variant="warning">{previewError}</Notice>}

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Kjør synkronisering</h3>
                    <p className={styles.sectionHelp}>
                        Legger en synkjobb i køen nå — en worker plukker opp jobben.
                    </p>
                    <SyncTrigger bindingId={binding.bindingId} contextLabel={binding.displayName} policy={policy} />
                </div>

                {policyAllows(policy, SyncDirection.AgolToCentral) && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Initial last</h3>
                        <p className={styles.sectionHelp}>
                            Koblingen synker bare endringer gjort etter at den ble opprettet. Hent alle
                            eksisterende objekter fra AGOL til sentralt datasett ved behov.
                        </p>
                        <InitialLoad bindingId={binding.bindingId} displayName={binding.displayName} />
                    </div>
                )}

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Tidsstyrt synk</h3>
                    <p className={styles.sectionHelp}>
                        Kjør synk automatisk på intervall eller cron, per retning. Kjøringene legges i samme kø.
                    </p>
                    <ScheduleEditor bindingId={binding.bindingId} policy={policy} />
                </div>

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Slett kobling</h3>
                    <p className={styles.sectionHelp}>
                        Stopper synkronisering for datasettet permanent. Handlingen kan ikke angres.
                    </p>
                    <div className={styles.rowActions}>
                        <Button
                            variant="danger"
                            size="sm"
                            leftIcon={<TrashIcon size={16} weight="bold" />}
                            onClick={() => setConfirmDelete(true)}
                        >
                            Slett kobling
                        </Button>
                    </div>
                </div>
            </div>

            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                title="Slette kobling?"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                            Avbryt
                        </Button>
                        <Button variant="danger" onClick={doDelete} disabled={deleteMutation.isPending}>
                            {deleteMutation.isPending ? 'Sletter…' : 'Slett kobling'}
                        </Button>
                    </>
                }
            >
                <p>
                    Er du sikker på at du vil slette koblingen <strong>{binding.displayName}</strong> ({binding.bindingId})?
                    Dette stopper synkronisering for datasettet. Handlingen kan ikke angres.
                </p>
                {deleteError && <Notice variant="danger">{deleteError}</Notice>}
            </Dialog>
        </Panel>
    );
};
